import React, { useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography
} from 'antd';
import type { TableColumnsType } from 'antd';
import type { TFunction } from 'i18next';
import { DeleteOutlined, EditOutlined, PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { ObjectCardEmpty, ObjectCardSection, ObjectCardTable } from '../layout/ObjectCard';
import { useTranslation } from 'react-i18next';
import VariantPicker from '../inventory/VariantPicker';
import { formatCurrencyUnitPrice } from '../../utils/currency';
import { UNIT_PRICE_INPUT_PROPS } from '../../utils/money';
import { formatCatalogProductDimensions } from '../../utils/productDimensions';
import { ORDER_CURRENCIES } from '../../modules/orders/currencies';
import {
  CustomerProductPrice,
  deleteCustomerProductPrice,
  fetchCustomerProductPrices,
  saveCustomerProductPrice,
  updateCustomerProductPrice
} from '../../modules/customers/api/customerProductPricesApi';
import { useFeedback } from '../../utils/feedback';

const { Text } = Typography;

interface CustomerProductPricesTabProps {
  customerId: string;
  canEdit: boolean;
}

interface PriceFormValues {
  productVariantId?: string;
  fixedPrice?: number;
  currency: string;
}

const describeVariant = (price: CustomerProductPrice, fallback: string) => {
  const dimensionLabel = formatCatalogProductDimensions(price) || '';
  const variantLabel = price.isDefault ? null : price.variantName;
  return [price.productName, variantLabel, dimensionLabel, price.productEan]
    .filter(Boolean)
    .join(' — ') || price.variantSku || fallback;
};

// The backend has no i18n and this route only ever throws plain English
// validation strings (see customerProductPriceService.js) — map the known
// ones to Polish copy so a raw backend string never reaches the operator
// (Product Language Rule); anything unmapped falls back to the caller's
// already-translated string.
const PRICE_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'Customer not found': 'customers:prices.errors.customerNotFound',
  'Price not found': 'customers:prices.errors.priceNotFound',
  'Invalid currency': 'customers:prices.errors.invalidCurrency',
  'productVariantId is required': 'customers:prices.errors.productVariantRequired',
  'fixedPrice must be a number between 0 and 1000000': 'customers:prices.errors.invalidFixedPrice'
};

function getPriceErrorMessage(error: any, t: TFunction, fallback: string): string {
  const serverMessage = error?.response?.data?.message;
  const key = typeof serverMessage === 'string' ? PRICE_ERROR_MESSAGE_KEYS[serverMessage] : undefined;
  return key ? t(key) : fallback;
}

const CustomerProductPricesTab: React.FC<CustomerProductPricesTabProps> = ({
  customerId,
  canEdit
}) => {
  const { t } = useTranslation(['customers', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [prices, setPrices] = useState<CustomerProductPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerProductPrice | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form] = Form.useForm<PriceFormValues>();

  const loadPrices = async () => {
    setLoading(true);
    try {
      const rows = await fetchCustomerProductPrices(customerId);
      setPrices(Array.isArray(rows) ? rows : []);
    } catch (error) {
      feedback.error(t('customers:prices.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // The Modal keeps its Form mounted (`forceRender`, no `destroyOnClose`) so
  // values seeded here always land on live fields. With `destroyOnClose` +
  // `preserve={false}` the Form remounted on open and rc-field-form reset the
  // previously unmounted fields, wiping the seeded product of an edited price
  // and blocking save (CUST-PRICE-EDIT-1).
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ currency: 'PLN' });
    setModalOpen(true);
  };

  const openEdit = (price: CustomerProductPrice) => {
    setEditing(price);
    form.resetFields();
    form.setFieldsValue({
      productVariantId: price.productVariantId,
      fixedPrice: price.fixedPrice,
      currency: price.currency
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (values: PriceFormValues) => {
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateCustomerProductPrice(customerId, editing.id, {
          fixedPrice: Number(values.fixedPrice),
          currency: values.currency
        });
        feedback.success(
          t('customers:prices.updateSuccess', {
            product: describeVariant(updated, t('customers:prices.unknownVariant'))
          })
        );
      } else {
        const created = await saveCustomerProductPrice(customerId, {
          productVariantId: values.productVariantId as string,
          fixedPrice: Number(values.fixedPrice),
          currency: values.currency
        });
        feedback.success(
          t('customers:prices.createSuccess', {
            product: describeVariant(created, t('customers:prices.unknownVariant'))
          })
        );
      }
      setModalOpen(false);
      setEditing(null);
      await loadPrices();
    } catch (error: any) {
      feedback.error(getPriceErrorMessage(error, t, t('customers:prices.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (price: CustomerProductPrice) => {
    const productLabel = describeVariant(price, t('customers:prices.unknownVariant'));
    modal.confirm({
      title: t('customers:prices.deleteConfirmTitle'),
      content: t('customers:prices.deleteConfirmDescription'),
      okText: t('common:yes'),
      cancelText: t('common:no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingId(price.id);
        try {
          await deleteCustomerProductPrice(customerId, price.id);
          feedback.success(t('customers:prices.deleteSuccess', { product: productLabel }));
          await loadPrices();
        } catch (error) {
          feedback.error(
            getPriceErrorMessage(
              error,
              t,
              t('customers:prices.deleteFailed', { product: productLabel })
            )
          );
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const columns: TableColumnsType<CustomerProductPrice> = [
    {
      title: t('customers:prices.product'),
      key: 'product',
      render: (_value, record) => describeVariant(record, t('customers:prices.unknownVariant'))
    },
    {
      title: t('customers:prices.fixedPrice'),
      key: 'fixedPrice',
      align: 'right',
      width: 200,
      render: (_value, record) => (
        <Text strong>{formatCurrencyUnitPrice(record.fixedPrice, record.currency)}</Text>
      )
    }
  ];

  if (canEdit) {
    columns.push({
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    });
  }

  return (
    <ObjectCardSection
      wide
      testId="customer-section-prices" tone="pink"
      title={t('customers:detail.pricesTab')}
      icon={<TagsOutlined />}
      count={loading ? undefined : prices.length}
      hint={t('customers:prices.description')}
      actions={canEdit && prices.length > 0 && (
        <Button size="small" icon={<PlusOutlined />} onClick={openCreate}>
          {t('customers:prices.addPrice')}
        </Button>
      )}
    >
      {!loading && prices.length === 0 ? (
        <ObjectCardEmpty
          icon={<TagsOutlined />}
          text={t('customers:prices.empty')}
          action={canEdit && (
            <Button size="small" icon={<PlusOutlined />} onClick={openCreate}>
              {t('customers:prices.addPrice')}
            </Button>
          )}
        />
      ) : (
        <ObjectCardTable<CustomerProductPrice>
          columns={columns}
          dataSource={prices}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      )}

      <Modal
        title={editing ? t('customers:prices.editTitle') : t('customers:prices.addTitle')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={saving}
        maskClosable={false}
        forceRender
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="productVariantId"
            label={t('customers:prices.product')}
            rules={[{ required: true, message: t('customers:prices.productRequired') }]}
          >
            <VariantPicker
              placeholder={t('customers:prices.selectVariant')}
              showInventory={false}
              disabled={Boolean(editing)}
            />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="fixedPrice"
              label={t('customers:prices.fixedPrice')}
              style={{ flex: 1, marginRight: 8 }}
              rules={[{ required: true, message: t('customers:prices.priceRequired') }]}
            >
              <InputNumber
                max={1000000}
                {...UNIT_PRICE_INPUT_PROPS}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="currency" label={t('customers:prices.currency')} style={{ width: 120 }}>
              <Select>
                {ORDER_CURRENCIES.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space.Compact>
        </Form>
      </Modal>
    </ObjectCardSection>
  );
};

export default CustomerProductPricesTab;
